import React from "react";
import { Helmet } from "react-helmet";
import { ExternalLink, TrendingDown, Plus } from "lucide-react";
import { createProductSlug } from "../utils/slugify";

const ProductDetailPage = ({ product, setSelectedProduct, setCurrentPage }) => {
  console.log("🔍 ProductDetailPage - product:", product);
  console.log("🔍 Retailer Search URLs:", product?.retailerSearchUrls);
  console.log("🔍 PSA URL:", product?.psaUrl);

  const handleAddToCompare = () => {
    // Get existing compare list from localStorage
    const existingCompare = JSON.parse(
      localStorage.getItem("compareList") || "[]"
    );

    // Check if product already in list
    if (existingCompare.find((p) => p.sku === product.sku)) {
      alert("Product already in comparison list");
      return;
    }

    // Check if at max (4 products)
    if (existingCompare.length >= 4) {
      alert("Maximum 4 products can be compared. Remove one first.");
      return;
    }

    // Add product
    existingCompare.push(product);
    localStorage.setItem("compareList", JSON.stringify(existingCompare));
    alert(`Added to comparison! (${existingCompare.length}/4)`);
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white dark:text-gray-900 text-center">
          <h2 className="text-2xl mb-4">Product not found</h2>
          <button
            onClick={() => setCurrentPage("browse")}
            className="px-6 py-2 bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Back to Browse
          </button>
        </div>
      </div>
    );
  }

  const currentPrice = product.currentPrice || 0;
  const originalPrice =
    product.originalPrice || product.msrp || currentPrice * 1.25;
  const savings = originalPrice - currentPrice;
  const savingsPercent =
    originalPrice > 0 ? ((savings / originalPrice) * 100).toFixed(0) : 0;

  const euroOpticPrice = {
    retailer: "EuroOptic",
    price: currentPrice,
    url: product.productUrl || "#",
    inStock: product.inStock,
  };

  const prices =
    product.prices && Array.isArray(product.prices)
      ? product.prices
      : [euroOpticPrice];
  const lowestPrice =
    prices.length > 0
      ? Math.min(...prices.map((p) => p.price || 0))
      : currentPrice;

  const hasDirectLink = (url) => {
    return url && url.trim() !== "" && url !== "#";
  };

  const allRetailers = [
    {
      name: "EuroOptic",
      url: product.productUrl || "https://www.eurooptic.com",
      hasPrice: true,
      price: currentPrice,
      inStock: product.inStock,
      isDirect: hasDirectLink(product.productUrl),
    },
    {
      name: "Palmetto State Armory",
      url:
        product.psaUrl ||
        product.retailerSearchUrls?.scheels ||
        "https://palmettostatearmory.com",
      hasPrice: false,
      isDirect: hasDirectLink(product.psaUrl),
    },
    {
      name: "Scheels",
      url: product.retailerSearchUrls?.scheels || "https://www.scheels.com",
      hasPrice: false,
      isDirect: false,
    },
    {
      name: "OpticsPlanet",
      url:
        product.retailerSearchUrls?.opticsPlanet ||
        "https://www.opticsplanet.com",
      hasPrice: false,
      isDirect: false,
    },
    {
      name: "Brownells",
      url: product.retailerSearchUrls?.brownells || "https://www.brownells.com",
      hasPrice: false,
      isDirect: false,
    },
    {
      name: "MidwayUSA",
      url: product.retailerSearchUrls?.midwayUsa || "https://www.midwayusa.com",
      hasPrice: false,
      isDirect: false,
    },
    {
      name: "Sportsman's Warehouse",
      url:
        product.retailerSearchUrls?.sportsmans || "https://www.sportsmans.com",
      hasPrice: false,
      isDirect: false,
    },
    {
      name: "Cabela's",
      url: product.retailerSearchUrls?.cabelas || "https://www.cabelas.com",
      hasPrice: false,
      isDirect: false,
    },
    {
      name: "Bass Pro Shops",
      url:
        product.retailerSearchUrls?.bassProShops || "https://www.basspro.com",
      hasPrice: false,
      isDirect: false,
    },
  ];

  const productSlug = createProductSlug(product);
  const canonicalUrl = `https://www.opticpicker.com/product/${productSlug}`;
  const productImage = product.imageUrl || product.image || "https://www.opticpicker.com/og-image.jpg";
  const metaDescription = `Compare prices on the ${product.name} from top retailers. Currently ${currentPrice > 0 ? `$${currentPrice.toFixed(2)} at EuroOptic` : "available"}. Find the best deal.`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "image": productImage,
    "description": product.description || `${product.name} — compare prices across top optics retailers.`,
    "sku": product.sku,
    "mpn": product.mpn,
    "brand": {
      "@type": "Brand",
      "name": product.manufacturer || product.brand || "Unknown"
    },
    "offers": {
      "@type": "Offer",
      "url": product.productUrl || canonicalUrl,
      "priceCurrency": "USD",
      "price": currentPrice.toFixed(2),
      "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "EuroOptic"
      }
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{product.name} - Best Price | OpticPicker</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={`${product.name} - Best Price | OpticPicker`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={productImage} />
        <meta property="og:type" content="product" />
        <meta property="product:price:amount" content={currentPrice.toFixed(2)} />
        <meta property="product:price:currency" content="USD" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => {
              setSelectedProduct(null);
              setCurrentPage("browse");
            }}
            className="flex items-center gap-2 text-gray-400 dark:text-gray-600 hover:text-white dark:hover:text-gray-900 transition"
          >
            ← Back to Results
          </button>

          <button
            onClick={handleAddToCompare}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition"
          >
            <Plus className="w-4 h-4" />
            Add to Compare
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          <div className="lg:col-span-2">
            <div className="bg-gray-800/40 dark:bg-white/60 backdrop-blur-sm border border-gray-700/50 dark:border-gray-300 rounded-2xl overflow-hidden">
              <img
                src={
                  product.imageUrl ||
                  product.image ||
                  "https://dummyimage.com/800x600/1f2937/9ca3af&text=No+Image"
                }
                alt={product.name}
                className="w-full h-96 object-contain"
                onError={(e) => {
                  e.target.src =
                    "https://dummyimage.com/800x600/1f2937/9ca3af&text=No+Image";
                }}
              />
            </div>
            <div className="mt-6">
              <h1 className="text-4xl font-bold text-white dark:text-gray-900 mb-2">
                {product.name}
              </h1>
              <p className="text-xl text-gray-400 dark:text-gray-600">
                {product.manufacturer || product.brand} • {product.category}
              </p>
              {product.mpn && (
                <p className="text-sm text-gray-500 dark:text-gray-600 mt-2">
                  MPN: {product.mpn}
                </p>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-green-900/40 to-gray-800/40 dark:from-green-100/40 dark:to-gray-200/40 backdrop-blur-sm border-2 border-green-500/30 dark:border-green-600/30 rounded-2xl p-8 sticky top-24">
              <p className="text-sm text-gray-400 dark:text-gray-600 uppercase tracking-wider mb-2">
                Best Price
              </p>
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-6xl font-bold text-green-400 dark:text-green-600">
                  ${lowestPrice.toFixed(2)}
                </span>
              </div>
              {originalPrice > currentPrice && (
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl text-gray-500 dark:text-gray-600 line-through">
                    ${originalPrice.toFixed(2)}
                  </span>
                </div>
              )}
              {savings > 0 && (
                <div className="bg-green-500/20 text-green-400 dark:text-green-600 px-4 py-3 rounded-full text-lg font-bold inline-flex items-center gap-2 w-full justify-center">
                  <TrendingDown className="w-5 h-5" />
                  Save ${savings.toFixed(2)} ({savingsPercent}% OFF)
                </div>
              )}
              {product.inStock !== undefined && (
                <div className="mt-4">
                  {product.inStock ? (
                    <div className="bg-green-500/20 text-green-400 dark:text-green-600 px-4 py-2 rounded-full text-sm font-semibold text-center">
                      ✓ In Stock
                    </div>
                  ) : (
                    <div className="bg-red-500/20 text-red-400 dark:text-red-600 px-4 py-2 rounded-full text-sm font-semibold text-center">
                      Out of Stock
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {product.description && (
          <div className="bg-gray-800/40 dark:bg-white/60 backdrop-blur-sm border border-gray-700/50 dark:border-gray-300 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-white dark:text-gray-900 mb-4">
              Description
            </h2>
            <p className="text-gray-300 dark:text-gray-700 leading-relaxed">
              {product.description}
            </p>
          </div>
        )}

        {/* Price History Placeholder */}
        {product.currentPrice && (
          <div className="bg-gray-800/40 dark:bg-white/60 backdrop-blur-sm border border-gray-700/50 dark:border-gray-300 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-white dark:text-gray-900 mb-4">
              Price History
            </h2>
            <div className="bg-gray-700/30 dark:bg-gray-200/50 rounded-xl p-8 text-center">
              <p className="text-gray-400 dark:text-gray-600 mb-2">
                Price tracking coming soon!
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-600">
                We're working on historical price data to help you find the best
                deals.
              </p>
              <div className="mt-4 h-48 bg-gray-800/50 dark:bg-gray-300/50 rounded-lg flex items-center justify-center">
                <p className="text-gray-600 dark:text-gray-500">
                  Graph Placeholder
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-gray-800/40 dark:bg-white/60 backdrop-blur-sm border border-gray-700/50 dark:border-gray-300 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white dark:text-gray-900 mb-6">
            Where to Buy
          </h2>
          <p className="text-sm text-gray-400 dark:text-gray-600 mb-6">
            {product.retailerSearchUrls
              ? "Compare prices at these retailers (🔍 = search link, direct links when available):"
              : "Check prices and availability at these retailers:"}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {allRetailers.map((retailer, idx) => (
              <a
                key={idx}
                href={retailer.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center justify-between gap-4 p-4 rounded-xl transition group ${
                  retailer.hasPrice
                    ? "bg-gradient-to-r from-blue-600/20 to-green-600/20 hover:from-blue-600/30 hover:to-green-600/30 border-2 border-blue-500/30"
                    : retailer.isDirect
                    ? "bg-gradient-to-r from-green-600/20 to-blue-600/20 hover:from-green-600/30 hover:to-blue-600/30 border-2 border-green-500/30"
                    : "bg-gray-700/30 dark:bg-gray-200/30 hover:bg-gray-700/50 dark:hover:bg-gray-200/50 border border-gray-600/30 dark:border-gray-400/30"
                }`}
              >
                <div>
                  <p
                    className={`font-semibold text-lg ${
                      retailer.hasPrice || retailer.isDirect
                        ? "text-white dark:text-gray-900"
                        : "text-gray-300 dark:text-gray-700"
                    }`}
                  >
                    {!retailer.isDirect && !retailer.hasPrice && "🔍 "}
                    {retailer.name}
                  </p>
                  {retailer.hasPrice && retailer.inStock !== undefined && (
                    <p
                      className={`text-xs ${
                        retailer.inStock
                          ? "text-green-400 dark:text-green-600"
                          : "text-red-400 dark:text-red-600"
                      }`}
                    >
                      {retailer.inStock ? "✓ In Stock" : "Out of Stock"}
                    </p>
                  )}
                  {!retailer.hasPrice && retailer.isDirect && (
                    <p className="text-xs text-green-400 dark:text-green-600">
                      Direct product link
                    </p>
                  )}
                  {!retailer.hasPrice && !retailer.isDirect && (
                    <p className="text-xs text-gray-500 dark:text-gray-600">
                      Search for this product
                    </p>
                  )}
                </div>
                <div className="text-right flex items-center gap-2">
                  {retailer.hasPrice ? (
                    <p className="text-2xl font-bold text-green-400 dark:text-green-600">
                      ${retailer.price.toFixed(2)}
                    </p>
                  ) : retailer.isDirect ? (
                    <p className="text-sm text-green-400 dark:text-green-600">
                      View →
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-600">
                      Search →
                    </p>
                  )}
                  <ExternalLink
                    className={`w-4 h-4 ${
                      retailer.hasPrice || retailer.isDirect
                        ? "text-blue-400 dark:text-blue-600"
                        : "text-gray-500 dark:text-gray-600"
                    }`}
                  />
                </div>
              </a>
            ))}
          </div>
          <div className="mt-6 p-4 bg-blue-500/10 dark:bg-blue-200/30 border border-blue-500/30 dark:border-blue-600/30 rounded-xl">
            <p className="text-sm text-blue-400 dark:text-blue-700">
              💡 Direct links go straight to the product. Search links (🔍) will
              search the retailer's site for "{product.manufacturer}{" "}
              {product.mpn}".
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
